import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchHistoricalFiles } from "@/services/api";
import { fetchProvidersCached } from "@/services/providersApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import type { HistoricalFile } from "@/types";
import { cn } from "@/lib/utils";
import {
  DEFAULT_HISTORY_FILTER_STATE,
  applyHistoryFilters,
  canDownloadMappedFile,
  providerOptionsForFiles,
  uniqueQualityOptions,
  uniqueSchemaOptions,
  type HistoryFilterState,
} from "@/lib/historyFilters";

import { PageBanner } from "@/components/reusable/PageBanner";
import { SkeletonTable, Skeleton } from "@/components/reusable/Skeleton";
import { HistoryFilterBar } from "@/components/history/HistoryFilterBar";
import { DownloadMappedFileButton } from "@/components/history/DownloadMappedFileButton";

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface HistoryPageProps {
  /** When set (Provider Profile tab), list is server-scoped to this provider. */
  providerId?: string;
  /** Display name for the scoped provider; avoids showing a raw UUID in the preview. */
  providerName?: string;
}

/**
 * Ingestion history browser.
 *
 * When `providerId` is set (Provider Profile tab), the page is server-scoped to
 * that provider and the Provider filter is hidden. Otherwise (global admin page)
 * all tenant files are loaded and client-side filters (provider-by-name, schema,
 * quality, sort) run via HistoryFilterBar.
 *
 * File Preview shows lifecycle + latest DQ summary fields. Gold-promoted files
 * (status=complete, quality_status=clean) also get Download mapped file.
 */
export function HistoryPage({ providerId, providerName }: HistoryPageProps = {}) {
  const { t } = useTranslation();
  const { track, trackError, startTimer } = useTelemetry();
  const isProviderScoped = Boolean(providerId);

  const [files, setFiles] = useState<HistoricalFile[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [filters, setFilters] = useState<HistoryFilterState>(DEFAULT_HISTORY_FILTER_STATE);
  const [providerNameById, setProviderNameById] = useState<Map<string, string>>(() => new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setSelectedFileId(null);
    setFilters(DEFAULT_HISTORY_FILTER_STATE);
    const stopTimer = startTimer();
    fetchHistoricalFiles(providerId)
      .then((data) => {
        setFiles(data);
        const providers = [...new Set(data.map((f) => f.providerId))];
        track("history_files_loaded", {
          fileCount: data.length,
          providerCount: providers.length,
          providerId: providerId ?? "all",
          durationMs: stopTimer(),
        });
      })
      .catch((err) => trackError(err, { context: "history_load" }))
      .finally(() => setLoading(false));
  }, [startTimer, track, trackError, providerId]);

  // Resolve provider UUIDs → display names for the global filter + preview only.
  useEffect(() => {
    if (isProviderScoped) return;
    fetchProvidersCached()
      .then((res) => {
        const map = new Map<string, string>();
        for (const p of res.items) {
          map.set(p.provider_id, p.provider_name || p.provider_short_code || p.provider_id);
        }
        setProviderNameById(map);
      })
      .catch((err) => trackError(err as Error, { context: "history_providers_load" }));
  }, [isProviderScoped, trackError]);

  const providerOptions = useMemo(
    () => providerOptionsForFiles(files, providerNameById),
    [files, providerNameById]
  );
  const schemaOptions = useMemo(() => uniqueSchemaOptions(files), [files]);
  const qualityOptions = useMemo(() => uniqueQualityOptions(files), [files]);

  const filteredFiles = useMemo(
    () => applyHistoryFilters(files, filters, { isProviderScoped }),
    [files, filters, isProviderScoped]
  );

  const selectedFile = selectedFileId
    ? (filteredFiles.find((f) => f.fileId === selectedFileId) ?? null)
    : null;

  const handleFiltersChange = useCallback(
    (next: HistoryFilterState) => {
      setFilters(next);
      setSelectedFileId(null);
      const count = applyHistoryFilters(files, next, { isProviderScoped }).length;
      track("history_filters_changed", {
        providerId: next.providerId,
        schemaId: next.schemaId,
        qualityStatus: next.qualityStatus,
        sortOrder: next.sortOrder,
        resultFileCount: count,
      });
    },
    [files, isProviderScoped, track]
  );

  const handleFileSelect = useCallback(
    (file: HistoricalFile) => {
      setSelectedFileId(file.fileId);
      track("history_file_selected", {
        provider: file.providerId || "unknown",
        fileId: file.fileId,
      });
    },
    [track]
  );

  /** Build the definition-list rows for the selected ingestion record. */
  const previewRows = (
    file: HistoricalFile
  ): Array<{ label: string; value: string; mono?: boolean }> => {
    const resolvedProviderName = providerNameById.get(file.providerId);
    const providerLabel = isProviderScoped
      ? providerName || file.providerId || "—"
      : resolvedProviderName || file.providerId || "—";
    const providerIsMono = isProviderScoped ? !providerName : !resolvedProviderName;

    const rows: Array<{ label: string; value: string; mono?: boolean }> = [
      { label: t("history.preview.fileName"), value: file.fileName || "—" },
      { label: t("history.preview.provider"), value: providerLabel, mono: providerIsMono },
      { label: t("history.preview.schema"), value: file.schemaId || "—", mono: true },
      { label: t("history.preview.status"), value: file.status || "—" },
      { label: t("history.preview.qualityStatus"), value: file.qualityStatus || "—" },
      { label: t("history.preview.submittedBy"), value: file.uploadedBy || "—" },
      { label: t("history.preview.submittedAt"), value: formatDate(file.createdAt) },
      { label: t("history.preview.updatedAt"), value: formatDate(file.updatedAt) },
    ];

    if (file.latestDqRunAt) {
      rows.push(
        { label: t("history.preview.latestDqRunAt"), value: formatDate(file.latestDqRunAt) },
        {
          label: t("history.preview.dqErrors"),
          value: file.dqErrorCount != null ? String(file.dqErrorCount) : "—",
        },
        {
          label: t("history.preview.dqWarnings"),
          value: file.dqWarningCount != null ? String(file.dqWarningCount) : "—",
        },
        {
          label: t("history.preview.dqCycles"),
          value: file.dqCycleCount != null ? String(file.dqCycleCount) : "—",
        }
      );
    } else {
      rows.push({
        label: t("history.preview.latestDqRunAt"),
        value: t("history.preview.noDqRun"),
      });
    }

    return rows;
  };

  return (
    <>
      {!isProviderScoped && (
        <PageBanner title={t("history.title")} subtitle={t("history.subtitle")} />
      )}

      {!loading && (
        <HistoryFilterBar
          filters={filters}
          onChange={handleFiltersChange}
          showProviderFilter={!isProviderScoped}
          providerOptions={providerOptions}
          schemaOptions={schemaOptions}
          qualityOptions={qualityOptions}
        />
      )}

      {loading ? (
        <div className="flex gap-6 animate-fade-in">
          <div className="w-72 shrink-0 space-y-2">
            <Skeleton width="w-24" height="h-4" />
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="h-10" />
            ))}
          </div>
          <div className="flex-1">
            <Skeleton width="w-32" height="h-4" className="mb-3" />
            <SkeletonTable rows={6} columns={2} />
          </div>
        </div>
      ) : (
        <div className="flex gap-6 animate-fade-in-up stagger-3">
          {/* File list — one entry per fileId */}
          <div className="w-72 shrink-0">
            <h3 className="text-sm font-semibold mb-3">{t("history.files")}</h3>
            <div className="space-y-1">
              {filteredFiles.length === 0 ? (
                <p className="text-sm text-[var(--color-text-tertiary)] py-4">
                  {t("history.noFiles")}
                </p>
              ) : (
                filteredFiles.map((file, i) => (
                  <button
                    key={file.fileId}
                    type="button"
                    onClick={() => handleFileSelect(file)}
                    className={cn(
                      "w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all duration-200 truncate",
                      selectedFileId === file.fileId
                        ? "bg-[var(--color-button-primary-bg)] text-white font-medium shadow-sm"
                        : "bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:translate-x-0.5"
                    )}
                    style={{ animationDelay: `${200 + i * 40}ms` }}
                  >
                    {file.fileName}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* File detail — lifecycle + DQ summary */}
          <div className="flex-1">
            <h3 className="text-sm font-semibold mb-3">{t("history.filePreview")}</h3>
            {selectedFile ? (
              <div className="rounded-xl border border-[var(--color-border-light)] overflow-hidden animate-scale-in bg-white">
                <div className="bg-[var(--color-bg-secondary)] px-4 py-3 border-b border-[var(--color-border-light)]">
                  <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                    {selectedFile.fileName}
                  </p>
                </div>
                <dl className="px-4 divide-y divide-[var(--color-border-light)]">
                  {previewRows(selectedFile).map(({ label, value, mono }) => (
                    <div
                      key={label}
                      className="flex items-center justify-between gap-4 py-2.5 text-sm"
                    >
                      <dt className="text-[var(--color-text-secondary)] shrink-0">{label}</dt>
                      <dd
                        className={cn(
                          "text-right text-[var(--color-text-primary)] break-all",
                          mono ? "font-mono text-xs" : "font-medium"
                        )}
                      >
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
                {canDownloadMappedFile(selectedFile) ? (
                  <div className="px-4 py-3 border-t border-[var(--color-border-light)]">
                    <DownloadMappedFileButton
                      fileId={selectedFile.fileId}
                      fallbackFileName={`${selectedFile.fileName.replace(/\.[^.]+$/, "") || selectedFile.fileName}_mapped.xlsx`}
                      data-testid={`history-download-mapped-${selectedFile.fileId}`}
                      onSuccess={(fileId) => track("history_mapped_file_downloaded", { fileId })}
                      onError={(err, fileId) =>
                        trackError(err, { context: "history_mapped_download", fileId })
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-[var(--color-text-tertiary)] py-8 text-center">
                {t("history.selectFile")}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
